import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';

import type { IPredicate, TPredicateKind } from '@feathr/blackbox';
import { FieldDataType } from '@feathr/blackbox';

import ComparisonSelect from '../ComparisonSelect';
import RuleSelect from '../RuleSelect';
import type { IAttrOption, IComparisonChange, TOptionsGroup } from '../utils';
import { findRuleOption, isEmailTrigger } from '../utils';
import ValueInput from '../ValueInput';

import * as styles from './Predicate.css';

interface IProps {
  /** The full field option lists, fetched once by the owner (see usePredicateOptions). */
  activities: IAttrOption[];
  attributes: IAttrOption[];
  disabled?: boolean;
  excludeIds?: string[];
  groupKind?: TPredicateKind;
  handleChangeComparison: (change: IComparisonChange) => void;
  handleChangeRule: (option: IAttrOption) => void;
  handleChangeValue: (value: IPredicate['value']) => void;
  optionsGroup: TOptionsGroup;
  predicate: IPredicate;
}

function Predicate({
  activities,
  attributes,
  disabled = false,
  excludeIds,
  handleChangeRule,
  handleChangeComparison,
  handleChangeValue,
  optionsGroup,
  predicate,
  groupKind,
}: Readonly<IProps>): JSX.Element {
  /*
   * Memoized so these arrays keep a stable identity across renders: they key the
   * rule derivation below and feed the child selects, both of which would
   * otherwise churn on every render (a value edit re-renders every predicate row).
   */
  const { activities: filteredActivities, attributes: filteredAttributes } = useMemo((): {
    activities: IAttrOption[];
    attributes: IAttrOption[];
  } => {
    /*
     * Email-campaign triggers restrict the options by trigger type: date-triggered
     * sends offer only date attributes, field-changed triggers (kind 'update')
     * offer person attributes, and activity triggers offer activities.
     */
    if (isEmailTrigger(optionsGroup)) {
      if (optionsGroup === 'emailCampaignDateTrigger') {
        return {
          activities: [],
          attributes: attributes.filter(({ type }) => type === FieldDataType.date),
        };
      }
      if (predicate.kind === 'update') {
        return { activities: [], attributes };
      }
      // Kind is activity or unset (fresh trigger on an activity-subtype campaign).
      return { activities, attributes: [] };
    }

    if (!groupKind) {
      return {
        activities,
        attributes,
      };
    }

    // If in a group, filter based on the group's kind
    if (groupKind === 'activity') {
      return {
        activities,
        attributes: [],
      };
    }
    return {
      activities: [],
      attributes,
    };
  }, [activities, attributes, groupKind, optionsGroup, predicate.kind]);

  /*
   * The selected option is derived from the predicate: deriveRuleChange writes
   * exactly the fields findRuleOption matches on, so a selection flows through
   * the owner's write and arrives back here on the next render.
   */
  const rule = useMemo(
    () =>
      predicate.attr_against
        ? findRuleOption(predicate, [...filteredActivities, ...filteredAttributes], optionsGroup)
        : undefined,
    [filteredActivities, filteredAttributes, optionsGroup, predicate],
  );

  const props = {
    activities: filteredActivities,
    attributes: filteredAttributes,
    disabled,
    excludeIds,
    optionsGroup,
    predicate,
    rule,
    handleSelectRule: handleChangeRule,
    handleChangeComparison,
    handleChangeValue,
  };

  const ruleSelect = <RuleSelect {...props} />;
  const comparison = <ComparisonSelect {...props} />;
  const value = <ValueInput {...props} />;
  return (
    <div className={styles.predicate}>
      {ruleSelect}
      {comparison}
      {value}
    </div>
  );
}

export default observer(Predicate);
